'use client';

import { useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { RiskLevel } from '@prisma/client';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { defaultFeedbackColor } from '~/config';
import Button from '../Button';
import Chip, { type ChipProps } from '../Chip';
import { cn } from '~/lib/utils';
import { RiskLevelIcon } from './RiskLevel';

export function formatVideoTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** One quote for a grouped finding when every policy cites the same text. */
export function sharedFeedbackText(items: Array<{ text?: string | null }>): string | null {
  if (items.length < 2) return null;
  const texts = items.map((item) => item.text?.trim() ?? '');
  const first = texts[0];
  if (!first || texts.some((text) => text !== first)) return null;
  return first;
}

export function buildFeedbackMeta(parts: Array<string | false | null | undefined>) {
  const text = parts.filter(Boolean).join(' · ');
  return text || null;
}

export function FeedbackItemFrame({
  children,
  onClick,
  isShadowFeedback,
  isPrimaryFeedback,
  isResolvedFeedback,
  isCompactView,
  className,
}: {
  children: ReactNode;
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
  isShadowFeedback?: boolean;
  isPrimaryFeedback?: boolean;
  isResolvedFeedback?: boolean;
  isCompactView?: boolean;
  className?: string;
}) {
  return (
    <div
      data-feedback-card
      className={cn(
        'border-border bg-card relative rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[opacity,border-color,box-shadow,background-color] duration-150',
        isCompactView ? 'space-y-2 p-2.5' : 'space-y-2.5 p-3.5',
        isShadowFeedback && 'border-sky-500/60',
        isPrimaryFeedback && 'border-emerald-500/60',
        isResolvedFeedback && 'opacity-70',
        onClick && 'cursor-pointer',
        onClick && 'hover:bg-accent/30 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
        onClick && !isShadowFeedback && !isPrimaryFeedback && 'hover:border-foreground/15',
        className,
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function FeedbackItemHeader({
  title,
  color,
  badge,
  meta,
  trailing,
  isCompactView,
}: {
  title: ReactNode;
  color?: string | null;
  badge?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  isCompactView?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2">
        <span
          className="size-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: color || defaultFeedbackColor }}
          aria-hidden
        />
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <h3
            className={cn(
              'text-foreground font-medium tracking-tight break-words',
              isCompactView ? 'text-xs leading-4' : 'text-[13px] leading-4',
            )}
          >
            {title}
          </h3>
          {badge ? <Chip level="neutral" text={badge} className="shrink-0 px-1.5 py-px" /> : null}
        </div>
        {meta ? (
          <p className="text-muted-foreground col-start-2 mt-0.5 truncate text-[11px] leading-4">
            {meta}
          </p>
        ) : null}
      </div>
      {trailing ? (
        <div className="flex shrink-0 flex-wrap items-start justify-end gap-1">{trailing}</div>
      ) : null}
    </div>
  );
}

export function FeedbackItemActions({
  leading,
  trailing,
}: {
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div
      className="flex items-center justify-between gap-2 pt-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-0.5 [&_button[data-size=icon-sm]]:text-muted-foreground [&_button[data-size=icon-sm]]:hover:text-foreground">
        {leading}
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-1.5">{trailing}</div> : null}
    </div>
  );
}

const riskChip: Partial<Record<RiskLevel, { level: ChipProps['level']; text: string }>> = {
  [RiskLevel.HIGH]: { level: 'alert', text: 'High' },
  [RiskLevel.MEDIUM]: { level: 'warning', text: 'Medium' },
  [RiskLevel.LOW]: { level: 'success', text: 'Low' },
};

export function FeedbackRiskChip({ riskLevel }: { riskLevel: RiskLevel | null | undefined }) {
  if (!riskLevel || riskLevel === RiskLevel.AUTO) return null;
  const chip = riskChip[riskLevel];
  if (!chip) return null;
  return (
    <Chip
      level={chip.level}
      text={chip.text}
      icon={<RiskLevelIcon riskLevel={riskLevel} />}
      className="gap-0.5 px-1.5 py-px"
    />
  );
}

export function FeedbackNewChip({ isNew }: { isNew?: boolean }) {
  if (!isNew) return null;
  return <Chip level="neutral" text="NEW" className="px-1.5 py-px" />;
}

export function FeedbackModeChips({
  isInShadowMode,
  isPrimaryFeedback,
  isShadowFeedback,
}: {
  isInShadowMode?: boolean;
  isPrimaryFeedback?: boolean;
  isShadowFeedback?: boolean;
}) {
  if (!isInShadowMode) return null;
  if (isPrimaryFeedback) return <Chip level="success" text="Primary" />;
  if (isShadowFeedback) return <Chip level="info" text="Shadow" />;
  return <Chip level="neutral" text="Original" />;
}

export function FeedbackFieldLabel({
  children,
  htmlFor,
  className,
}: {
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('text-foreground mb-1 block text-[13px] font-medium tracking-tight', className)}
    >
      {children}
    </label>
  );
}

export const teachPanelControlClassName =
  'min-h-16 rounded-md text-sm font-normal shadow-xs focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** A card inside a hidden tab reports a 0×0 box at the origin. */
function visibleFeedbackCard(column: HTMLElement) {
  const cards = column.querySelectorAll<HTMLElement>('[data-feedback-card]');
  for (const card of cards) {
    if (card.closest('[hidden]')) continue;
    const rect = card.getBoundingClientRect();
    if (rect.width > 1) return card;
  }
  return null;
}

/** Column content box used when no finding card is on screen. */
function columnContentBox(column: HTMLElement) {
  const scroll =
    [...column.children].find(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child.classList.contains('overflow-y-auto'),
    ) ?? column;
  const rect = scroll.getBoundingClientRect();
  const style = getComputedStyle(scroll);
  const padLeft = Number.parseFloat(style.paddingLeft) || 0;
  const padRight = Number.parseFloat(style.paddingRight) || 0;
  return {
    left: rect.left + padLeft,
    width: Math.max(0, rect.width - padLeft - padRight),
  };
}

/** Keeps the shade glued to the panel. Card scroll does not move it. */
function placeTeachShade(column: HTMLElement) {
  const panel = column.querySelector<HTMLElement>('[data-teach-panel] .pointer-events-auto');
  if (!panel) return;
  const columnRect = column.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const anchor = visibleFeedbackCard(column)?.getBoundingClientRect() ?? columnContentBox(column);
  column.style.setProperty('--teach-panel-top', `${(panelRect.top - columnRect.top).toFixed(1)}px`);
  column.style.setProperty('--teach-panel-left', `${(anchor.left - columnRect.left).toFixed(1)}px`);
  column.style.setProperty('--teach-panel-width', `${anchor.width.toFixed(1)}px`);
  column.style.setProperty('--teach-panel-radius', getComputedStyle(panel).borderTopLeftRadius);
}

export function FeedbackTeachPanel({
  title,
  description,
  onClose,
  children,
  footer,
  className,
}: {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [column, setColumn] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    setColumn(anchorRef.current?.closest<HTMLElement>('[data-feedback-column]') ?? null);
  }, []);

  useLayoutEffect(() => {
    if (!column) return;
    let frame = 0;
    const place = () => {
      frame = 0;
      placeTeachShade(column);
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(place);
    };
    place();
    const panel = column.querySelector('[data-teach-panel]');
    const observer = new ResizeObserver(schedule);
    if (panel) observer.observe(panel);
    observer.observe(column);
    const card = visibleFeedbackCard(column);
    if (card) observer.observe(card);
    window.addEventListener('resize', schedule);
    const appear = window.requestAnimationFrame(() => {
      column.style.setProperty('--teach-shade', '1');
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(appear);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      column.style.removeProperty('--teach-shade');
      column.style.removeProperty('--teach-panel-top');
      column.style.removeProperty('--teach-panel-left');
      column.style.removeProperty('--teach-panel-width');
      column.style.removeProperty('--teach-panel-radius');
    };
  }, [column]);

  const panel = (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 z-10 transition-opacity duration-150 motion-reduce:transition-none"
        style={{
          opacity: 'var(--teach-shade, 0)',
          left: 'var(--teach-panel-left, 0px)',
          width: 'var(--teach-panel-width, 0px)',
          height: 'calc(var(--teach-panel-top, 0px) + var(--teach-panel-radius, 0px))',
          background:
            'linear-gradient(to bottom, transparent calc(var(--teach-panel-top, 0px) - 4.5rem), rgb(0 0 0 / 0.2) var(--teach-panel-top, 0px))',
          maskImage:
            'radial-gradient(circle at var(--teach-panel-radius, 0px) 100%, transparent var(--teach-panel-radius, 0px), rgb(0 0 0) var(--teach-panel-radius, 0px)), radial-gradient(circle at calc(100% - var(--teach-panel-radius, 0px)) 100%, transparent var(--teach-panel-radius, 0px), rgb(0 0 0) var(--teach-panel-radius, 0px))',
          maskComposite: 'intersect',
        }}
      />
      <div
        data-teach-panel
        className="pointer-events-none absolute inset-x-0 bottom-0 z-200 flex max-h-full flex-col justify-end"
      >
        <div
          className={cn(
            'border-border bg-card pointer-events-auto relative z-10 flex max-h-full flex-col overflow-hidden rounded-xl rounded-bl-none border shadow-[0_-8px_14px_-12px_rgba(0,0,0,0.28)]',
            className,
          )}
          style={{
            marginLeft: 'var(--teach-panel-left, 0px)',
            width: 'var(--teach-panel-width, 100%)',
            maxWidth: 'var(--teach-panel-width, none)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-border/80 bg-card shrink-0 flex items-start justify-between gap-3 border-b px-3.5 pt-3.5 pb-2.5">
            <div className="min-w-0 pt-0.5">
              <h3 className="text-foreground text-sm leading-5 font-semibold tracking-tight">
                {title}
              </h3>
              {description ? (
                <p className="text-muted-foreground mt-1 text-[13px] leading-relaxed">
                  {description}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Close"
              onClick={onClose}
            >
              <XMarkIcon className="size-4" strokeWidth={2} />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pt-3 pb-3.5">{children}</div>
          {footer ? (
            <div className="border-border/80 bg-card flex w-full shrink-0 items-center gap-2 border-t px-3.5 py-3">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <>
      <span ref={anchorRef} className="hidden" />
      {column ? createPortal(panel, column) : panel}
    </>
  );
}
